import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import { Theme, ThemeOptions } from '@mui/material';

export const BORDER_CONFIG = '0.5px solid #000000';

export const menuStyle = (theme: Theme) => ({
  transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1) !important',
  transformOrigin: 'top center !important',
  '& .MuiPaper-root': {
    animation: 'menuSlideDown 400ms cubic-bezier(0.4, 0, 0.2, 1) !important',
    backgroundColor: 'white',
    borderRadius: '13px',
    border: BORDER_CONFIG,
    boxShadow: 'none',
    '&::-webkit-scrollbar': {
      width: '4px'
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '10px',
      backgroundColor: theme.palette.grey[200],
      marginTop: '12px',
      marginBottom: '12px'
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
      backgroundColor: '#000000'
    },
    '.MuiMenu-list': {
      boxShadow: 'none',
      '& .MuiMenuItem-root': {
        display: 'flex',
        gap: '4px'
      }
    }
  }
});

export const styleOverridesAutocomplete = {
  endAdornment: {
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    position: 'absolute'
  },
  root: {
    '& label.MuiFormLabel-root.MuiInputLabel-root:not(.MuiFormLabel-filled):not(.Mui-focused):not(.MuiInputLabel-shrink)':
      {
        marginLeft: '8px'
      }
  },
  input: {
    marginLeft: '8px'
  }
};

export const styleOverridesSelect = {
  select: {
    paddingRight: '30px',
    paddingLeft: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    height: '37px',
    boxSizing: 'border-box' as const,
    '&.MuiInputBase-inputSizeSmall': {
      height: '31px'
    }
  },
  icon: {
    right: '10px',
    width: '1.25rem'
  }
};

export const merginSelectPaperStyle = {
  '&:has(ul)': {
    '&:has(li)': {
      '&:not(:has(li:nth-of-type(6)))': {
        marginTop: -5.5
      }
    }
  }
};

export const commonComponentsStyles: NonNullable<ThemeOptions['components']> = {
  MuiTextField: {
    variants: [
      {
        props: { multiline: true },
        style: {
          display: 'contents',
          // Change border radius for inner input base in multiline variant
          '& .MuiInputBase-root': {
            borderRadius: 25
          }
        }
      }
    ]
  },
  MuiButton: {
    styleOverrides: {
      root: {
        height: '37px',
        boxShadow: 'none',
        '&.MuiButton-sizeSmall': {
          height: '31px'
        }
      },
      contained: {
        color: 'white'
      }
    }
  },
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        borderRadius: '50px',
        lineHeight: '166%',
        ...(ownerState?.multiline && {
          overflow: 'auto'
        }),
        '& input': {
          paddingTop: '7px',
          paddingBottom: '7px'
        },
        backgroundColor: theme.palette.background.default,
        '&.MuiInputBase-sizeSmall': {
          fontSize: '0.875rem',
          '& input': {
            paddingTop: '5.5px',
            paddingBottom: '5.5px'
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem'
          }
        }
      })
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        border: BORDER_CONFIG
      },
      root: {
        borderRadius: '50px',
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: BORDER_CONFIG
        }
      }
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      shrink: {
        color: '#000000 !important',
        marginLeft: 'calc(3% - 0rem)',
        '&.Mui-disabled': {
          color: '#00000038 !important'
        },
        '& + .MuiOutlinedInput-root fieldset > legend': {
          paddingLeft: '3%',
          marginLeft: '3%'
        }
      },
      root: {
        '&:not(.MuiFormLabel-filled):not(.Mui-focused)': {
          '&.Mui-disabled': {
            color: '#00000038'
          },
          '&.MuiInputLabel-sizeSmall': {
            fontSize: '0.875rem'
          }
        }
      }
    }
  },
  MuiMenu: {
    styleOverrides: {
      root: ({ theme }) => menuStyle(theme)
    }
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        backgroundColor: theme.palette.info.dark,
        color: theme.palette.common.white
      }),
      arrow: ({ theme }) => ({
        color: theme.palette.grey[100]
      })
    }
  },
  MuiSlider: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        '& .MuiSlider-thumb': {
          marginRight: ownerState.size === 'medium' ? '-20px' : '-12px'
        },
        '& .MuiSlider-mark': {
          display: 'none'
        },
        '& .MuiSlider-markLabel': {
          marginRight: '-2ch',
          fontSize: '0.75rem'
        },
        '& .MuiSlider-rail': {
          backgroundColor: theme.palette.background.paper
        }
      })
    },
    defaultProps: {
      size: 'medium',
      valueLabelDisplay: 'auto'
    }
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: BORDER_CONFIG
      }
    }
  },
  MuiChip: {
    defaultProps: {
      size: 'small'
    }
  },
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        borderRadius: '25px',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#555'
          }
        },
        '&::-webkit-scrollbar-corner': {
          backgroundColor: '#f1f1f1'
        }
      },
      '*': {
        '&::-webkit-scrollbar': {
          width: '6px',
          height: '6px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '3px'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#c1c1c1',
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: '#a8a8a8'
          }
        }
      }
    }
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        p: { xs: 1.5, md: 2 }
      }
    }
  }
};
